import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import API_ENDPOINTS from "../ApiEndPoints";

// Reusable responsive grid style (auto-fit cards)
const gridListStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  gap: 12,
};

const Section = ({ title, children, right }) => (
  <div className="panel with-rounded-corner panel-default">
    <div className="panel-heading" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <h3 className="panel-title" style={{ margin: 0 }}>{title}</h3>
      <div className="panel-heading-btn">{right}</div>
    </div>
    <div className="panel-collapse in">
      <div className="panel-body">{children}</div>
    </div>
  </div>
);

function useAsync(asyncFn, deps = []) {
  const [state, setState] = useState({ loading: false, error: null, data: null });
  const run = async (...args) => {
    try {
      setState({ loading: true, error: null, data: null });
      const res = await asyncFn(...args);
      setState({ loading: false, error: null, data: res });
      return res;
    } catch (e) {
      setState({ loading: false, error: e, data: null });
      throw e;
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => ({ ...state, run }), [state.loading, state.error, state.data]);
}

const UnitCard = ({ unit, onEdit, onDelete, onAddRole, onOpenRole, editorElement }) => (
  <div className="panel with-rounded-corner panel-default" style={{ borderLeft: `5px solid ${unit.color || "#888"}` }}>
    <div className="panel-heading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div>
        <h3 className="panel-title" style={{ margin: 0 }}>{unit.title}</h3>
        <sub className="hovertocopy" data-id={unit.tid}><a href="#" title={unit.tid}>Click to get TID</a></sub>
      </div>
      <div className="panel-heading-btn">
        <button className="btn" title="Edit" onClick={() => onEdit(unit)}>
          <i className="fa fa-pencil" />
        </button>
        <button className="btn" title="Delete" onClick={() => onDelete(unit)}>
          <i className="fa fa-trash" />
        </button>
      </div>
    </div>
    <div className="panel-collapse in">
      <div className="panel-body">
        <p dangerouslySetInnerHTML={{ __html: unit.description || "" }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ display: 'inline-block', width: 10, height: 10, background: unit.color || '#888', borderRadius: 2 }} />
          <small style={{ color: '#666' }}>{Array.isArray(unit.roles) ? unit.roles.length : 0} role(s)</small>
        </div>
        <h5 style={{ marginTop: 10 }}>{unit.title} Roles</h5>
        {unit.roles && unit.roles.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
            {unit.roles.map((r) => (
              <div key={r.id} className="well m-b-0" style={{ padding: 8 }}>
                <a href="#" onClick={(e) => { e.preventDefault(); onOpenRole(r); }}>{r.title}</a>
              </div>
            ))}
          </div>
        ) : (
          <div className="well m-b-10"> <h5>No roles in this unit, add one.</h5> </div>
        )}
        <button className="btn btn-link" onClick={() => onAddRole(unit)}><i className="fa fa-plus" /> Add role</button>
        {editorElement}
      </div>
    </div>
  </div>
);

// Helpers to format task details for richer list/table display
const freqLabel = (f) => ({
  1: 'On Trigger',
  2: 'On Button',
  3: 'Manual',
  7: 'First Work Day',
  8: 'Last Work Day',
  10: 'Monthly',
  11: 'Weekly',
  14: 'Day of Month',
  15: 'Yearly',
}[Number(f)] || '—');

const formatDue = (t) => {
  if (t?.due_option === 'user_select') return 'User selects date';
  if (t?.due_number && t?.due_period) return `${t.due_number} ${String(t.due_period).toLowerCase()}`;
  return 'Auto';
};

const weekdaysShort = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const formatTaskSummary = (t) => {
  const parts = [];
  const f = freqLabel(t?.create_frequency);
  if (f && f !== '—') parts.push(f);
  if (Array.isArray(t?.taskdays) && t.taskdays.length) {
    const d = t.taskdays.map((n) => weekdaysShort[(Number(n) - 1 + 7) % 7]).join(', ');
    parts.push(d);
  }
  if (t?.weekinmonth && t?.dayofweek) parts.push(`Week ${t.weekinmonth} · DOW ${t.dayofweek}`);
  if (t?.date_start) parts.push(`Day ${t.date_start}`);
  if (t?.yearmonth && t?.yearday) parts.push(`Month ${t.yearmonth} · Day ${t.yearday}`);
  parts.push(`Due: ${formatDue(t)}`);
  if (t?.followstask) parts.push(`After: ${t.followstask}`);
  if (t?.form_tid) parts.push(`Form: ${t.form_tid}`);
  if (t?.list_tid) parts.push(`List: ${t.list_tid}`);
  return parts.join(' • ');
};

const RoleView = ({ role, onBack, onEdit, onDelete, onAddTask, onOpenTask, editorElement, tasksViewMode, setTasksViewMode }) => (
  <div className="panel with-rounded-corner panel-default">
    <div className="panel-heading">
      <div className="panel-heading-btn">
        <button className="btn" onClick={() => onEdit(role)} title="Edit"><i className="fa fa-pencil" /></button>
        <button className="btn" onClick={() => onDelete(role)} title="Delete"><i className="fa fa-trash" /></button>
      </div>
      <a href="#" onClick={(e) => { e.preventDefault(); onBack(); }}>Back</a>
      <h4 className="panel-title">{role.title} (ROLE)</h4>
      <sub className="hovertocopy" data-id={role.tid}><a href="#" title={role.tid}>Click to get TID</a></sub>
    </div>
    <div className="panel-collapse in">
      <div className="panel-body">
        <div style={{ marginBottom: 10 }}>
          <p dangerouslySetInnerHTML={{ __html: role.description || "" }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
          <div>
            {(() => {
              const names = role.assigned_to_names || (role.assigned_to && typeof role.assigned_to === 'object' ? Object.values(role.assigned_to) : Array.isArray(role.assigned_to) ? role.assigned_to : []);
              return names && names.length > 0 ? (
                <div><strong>Assign To:</strong> {names.join(', ')}</div>
              ) : null;
            })()}
            {(() => {
              const names = role.reviewed_by_names || (role.reviewed_by && typeof role.reviewed_by === 'object' ? Object.values(role.reviewed_by) : Array.isArray(role.reviewed_by) ? role.reviewed_by : []);
              return names && names.length > 0 ? (
                <div><strong>Review by:</strong> {names.join(', ')}</div>
              ) : null;
            })()}
          </div>
          <div>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <h5 className="m-b-0">{role.title} Tasks</h5>
              <div>
                <span className="m-r-10">View:</span>
                <div className="btn-group">
                  <button className={`btn btn-xs ${tasksViewMode==='list'?'btn-primary':'btn-default'}`} onClick={() => setTasksViewMode('list')}>List</button>
                  <button className={`btn btn-xs ${tasksViewMode==='table'?'btn-primary':'btn-default'}`} onClick={() => setTasksViewMode('table')}>Table</button>
                </div>
              </div>
            </div>
            {role.tasks && role.tasks.length > 0 ? (
              tasksViewMode === 'list' ? (
                <div style={gridListStyle}>
                  {role.tasks.map((t) => (
                    <div key={t.id} className="well m-b-0" style={{ padding: 10 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <a href="#" onClick={(e) => { e.preventDefault(); onOpenTask(t); }}>{t.title}</a>
                        <small style={{ color:'#999' }}>{t.tid}</small>
                      </div>
                      <div style={{ marginTop: 6 }}>
                        <small style={{ color:'#555' }}>{formatTaskSummary(t)}</small>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>TID</th>
                        <th>Frequency</th>
                        <th>Due</th>
                        <th>Type</th>
                        <th>Form/List</th>
                        <th>Active</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {role.tasks.map((t) => (
                        <tr key={t.id}>
                          <td><a href="#" onClick={(e) => { e.preventDefault(); onOpenTask(t); }}>{t.title}</a></td>
                          <td>{t.tid}</td>
                          <td>{freqLabel(t.create_frequency)}</td>
                          <td>{formatDue(t)}</td>
                          <td>{Number(t.record_kpi_type) === 4 ? 'Form' : Number(t.record_kpi_type) === 3 ? 'Yes/No' : '—'}</td>
                          <td>{t.form_tid || '—'} {t.list_tid ? ` / ${t.list_tid}` : ''}</td>
                          <td>{t.active ? 'Yes' : 'No'}</td>
                          <td>
                            <button className="btn btn-xs btn-default" onClick={() => onOpenTask(t)}><i className="fa fa-eye" /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              <div className="text-center well m-b-10"><h5>No tasks yet, add one</h5></div>
            )}
            <button className="btn btn-link" onClick={() => onAddTask(role)}><i className="fa fa-plus" /> Add task</button>
            {editorElement}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const TaskView = ({ task, onBack, history, onEdit, onDelete, editorElement }) => {
  const [formLink, setFormLink] = useState(null);

  useEffect(() => {
    const type = Number(task?.record_kpi_type);
    if (type === 4 && task?.form_tid) {
      (async () => {
        try {
          const res = await axios.get(API_ENDPOINTS.forms?.all || '/api/v1/forms/all');
          const forms = res?.data?.data || res?.data || [];
          const f = forms.find((x) => x.tid === task.form_tid);
          setFormLink(f?.id ? `/forms/edit/${f.id}` : null);
        } catch (e) {
          setFormLink(null);
        }
      })();
    } else {
      setFormLink(null);
    }
  }, [task?.record_kpi_type, task?.form_tid]);

  return (
  <div className="panel with-rounded-corner panel-default">
    <div className="panel-heading">
      <div className="panel-heading-btn">
        <button className="btn" onClick={() => onEdit(task)} title="Edit"><i className="fa fa-pencil" /></button>
        <button className="btn" onClick={() => onDelete(task)} title="Delete"><i className="fa fa-trash" /></button>
      </div>
      <a href="#" onClick={(e) => { e.preventDefault(); onBack(); }}>Back</a>
      <h4 className="panel-title">{task.title}</h4>
      <sub className="hovertocopy" data-id={task.tid}><a href="#" title={task.tid}>Click to get TID</a></sub>
    </div>
    <div className="panel-collapse in">
      <div className="panel-body">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
          <div>
            <p dangerouslySetInnerHTML={{ __html: task.description || "" }} />
          </div>
          <div>
            <div><strong>Frequency:</strong> {freqLabel(task.create_frequency)}</div>
            <div><strong>Type:</strong> {Number(task.record_kpi_type) === 4 ? 'Form' : Number(task.record_kpi_type) === 3 ? 'Yes/No' : '—'}</div>
            {Array.isArray(task.taskdays) && task.taskdays.length > 0 && (
              <div><strong>Days:</strong> {task.taskdays.join(', ')}</div>
            )}
            {task.days_tags && <div><strong>Days (tags):</strong> <span dangerouslySetInnerHTML={{__html: task.days_tags}} />
            </div>}
            <div><strong>Reviewable:</strong> {task.reviewable ? 'Yes' : 'No'}</div>
            {Number(task.record_kpi_type) === 4 && task.form_tid && (
              <div>
                <strong>Form:</strong> {task.form_tid}
                {formLink && (
                  <>
                    {' '}<a href={formLink} target="_blank" rel="noopener noreferrer">Open</a>
                  </>
                )}
              </div>
            )}
            {task.list_tid && <div><strong>List:</strong> {task.list_tid}</div>}
            <div><strong>Due:</strong> {formatDue(task)}</div>
            {task.weekinmonth && <div><strong>Week in Month:</strong> {task.weekinmonth}</div>}
            {task.dayofweek && <div><strong>Day of Week:</strong> {task.dayofweek}</div>}
            {task.date_start && <div><strong>Date Start:</strong> {task.date_start}</div>}
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <h5>History</h5>
          {history.loading && <div>Loading…</div>}
          {history.error && <div className="text-danger">{history.error.message}</div>}
          {history.data && history.data.data && history.data.data.length === 0 && <div>No history yet.</div>}
          {history.data && history.data.data && history.data.data.length > 0 && (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Status</th>
                    <th>Completed</th>
                    <th>Start</th>
                    <th>Due</th>
                    <th>Completed At</th>
                  </tr>
                </thead>
                <tbody>
                  {history.data.data.map((h) => (
                    <tr key={h.id}>
                      <td>{h.id}</td>
                      <td>{h.title}</td>
                      <td>{h.status}</td>
                      <td>{h.completed}</td>
                      <td>{h.date_start}</td>
                      <td>{h.date_due}</td>
                      <td>{h.date_completed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {editorElement}
      </div>
    </div>
  </div>
  );
};

// Lightweight Rich Text editor using contenteditable and DOMPurify
const ToolbarButton = ({ cmd, label, arg }) => (
  <button
    type="button"
    className="btn btn-xs btn-default m-r-5"
    onClick={(e) => {
      e.preventDefault();
      document.execCommand(cmd, false, arg);
    }}
  >{label}</button>
);

const RichTextField = ({ label, value, onChange, placeholder }) => {
  const ref = useRef(null);
  const [height, setHeight] = useState(320);
  const dragging = useRef(false);
  const startY = useRef(0);
  const startH = useRef(320);
  useEffect(() => {
    if (ref.current && value !== ref.current.innerHTML) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);
  const handleInput = () => {
    const html = ref.current?.innerHTML || "";
    const clean = DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
    onChange(clean);
  };
  const onMouseDown = (e) => {
    dragging.current = true;
    startY.current = e.clientY;
    startH.current = height;
    e.preventDefault();
  };
  const onMouseMove = (e) => {
    if (!dragging.current) return;
    const dy = e.clientY - startY.current;
    const next = Math.max(200, startH.current + dy);
    setHeight(next);
  };
  const onMouseUp = () => { dragging.current = false; };
  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  });
  return (
    <div className="form-group">
      {label && <label>{label}</label>}
      <div className="m-b-5">
        <ToolbarButton cmd="bold" label={<i className="fa fa-bold" />} />
        <ToolbarButton cmd="italic" label={<i className="fa fa-italic" />} />
        <ToolbarButton cmd="underline" label={<i className="fa fa-underline" />} />
        <ToolbarButton cmd="insertUnorderedList" label={<i className="fa fa-list-ul" />} />
        <ToolbarButton cmd="insertOrderedList" label={<i className="fa fa-list-ol" />} />
        <ToolbarButton cmd="formatBlock" arg="H3" label={<span>H3</span>} />
        <ToolbarButton cmd="removeFormat" label={<i className="fa fa-eraser" />} />
      </div>
      <div
        ref={ref}
        className="form-control"
        contentEditable
        style={{ minHeight: height, height, overflowY: "auto", width: '100%' }}
        onInput={handleInput}
        data-placeholder={placeholder || "Type here…"}
      />
      <div
        onMouseDown={onMouseDown}
        style={{ cursor: 'ns-resize', height: 10, background: '#f3f3f3', border: '1px solid #eee', borderTop: 0 }}
        title="Drag to resize"
      />
    </div>
  );
};

const TextField = ({ label, value, onChange, placeholder, type = "text" }) => (
  <div className="form-group">
    {label && <label>{label}</label>}
    <input type={type} className="form-control" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
  </div>
);

const CheckboxField = ({ label, checked, onChange, id }) => {
  const localId = useRef(id || `chk_${Math.random().toString(36).slice(2)}`);
  return (
    <div className="form-check m-b-10">
      <input
        id={localId.current}
        className="form-check-input"
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{
          // Ensure visibility even if theme hides native inputs
          position: 'static',
          opacity: 1,
          appearance: 'auto',
          WebkitAppearance: 'checkbox',
          MozAppearance: 'checkbox',
          marginRight: 6,
          cursor: 'pointer',
        }}
      />
      <label className="form-check-label" htmlFor={localId.current} style={{ cursor: 'pointer' }}>
        {label}
      </label>
    </div>
  );
};

const SelectField = ({ label, value, onChange, options }) => (
  <div className="form-group" style={{ position: 'relative' }}>
    {label && <label>{label}</label>}
    <select
      className="manual-select form-control"
      style={{ width: '100%', maxWidth: '100%', backgroundColor: '#fff', color: '#333', pointerEvents: 'auto', WebkitAppearance: 'menulist', MozAppearance: 'menulist' }}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">—</option>
      {options.map((o) => (
        <option key={String(o.value)} value={o.value}>{o.label}</option>
      ))}
    </select>
  </div>
);

// Button-based weekday selector to avoid CSS conflicts with checkboxes
const WeekdayToggleGroup = ({ label, value = [], onChange }) => {
  const days = [
    { value: 1, short: 'Mon', label: 'Monday' },
    { value: 2, short: 'Tue', label: 'Tuesday' },
    { value: 3, short: 'Wed', label: 'Wednesday' },
    { value: 4, short: 'Thu', label: 'Thursday' },
    { value: 5, short: 'Fri', label: 'Friday' },
    { value: 6, short: 'Sat', label: 'Saturday' },
    { value: 7, short: 'Sun', label: 'Sunday' },
  ];
  const normalized = (Array.isArray(value) ? value : []).map((v) => Number(v));
  const toggle = (d) => {
    const dv = Number(d);
    const exists = normalized.includes(dv);
    const next = exists ? normalized.filter((x) => x !== dv) : [...normalized, dv];
    onChange(next.sort((a,b)=>a-b));
  };
  return (
    <div className="form-group">
      {label && <label>{label}</label>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {days.map((d) => {
          const active = normalized.includes(d.value);
          return (
            <button
              key={d.value}
              type="button"
              className={`btn btn-xs ${active ? 'btn-primary' : 'btn-default'}`}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              aria-pressed={active}
              onClick={() => toggle(d.value)}
              title={d.label}
            >
              {d.short}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const EditorPanel = ({ editor, onClose, onSaved, selectedRole, reloadUnits, reopenContexts }) => {
  if (!editor) return null;
  const isEdit = editor.mode === "edit";
  const [title, setTitle] = useState(editor.data?.title || "");
  const [color, setColor] = useState(editor.type === "unit" ? (editor.data?.color || "#2d8cff") : undefined);
  const [description, setDescription] = useState(editor.data?.description || "");
  // Role fields
  const [assignedUsers, setAssignedUsers] = useState(
    editor.type === 'role'
      ? (() => {
          const ids = editor.data?.assigned_to_ids ?? null;
          if (Array.isArray(ids)) return ids.map(String);
          const map = editor.data?.assigned_to ?? {};
          if (map && typeof map === 'object') return Object.keys(map).map(String);
          return [];
        })()
      : []
  );
  const [reviewUsers, setReviewUsers] = useState(
    editor.type === 'role'
      ? (() => {
          const ids = editor.data?.reviewed_by_ids ?? null;
          if (Array.isArray(ids)) return ids.map(String);
          const map = editor.data?.reviewed_by ?? {};
          if (map && typeof map === 'object') return Object.keys(map).map(String);
          return [];
        })()
      : []
  );
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  // Task fields
  const [createFrequency, setCreateFrequency] = useState(editor.type === "task" ? (editor.data?.create_frequency ?? "") : "");
  const [taskdays, setTaskdays] = useState(
    editor.type === "task" ? ((editor.data?.taskdays ?? []).map((v) => Number(v))) : []
  );
  const [reviewable, setReviewable] = useState(editor.type === "task" ? (!!editor.data?.reviewable) : false);
  const [formTid, setFormTid] = useState(editor.type === "task" ? (editor.data?.form_tid ?? "") : "");
  const [listTid, setListTid] = useState(editor.type === "task" ? (editor.data?.list_tid ?? "") : "");
  const [dueOption, setDueOption] = useState(editor.type === "task" ? (editor.data?.due_option ?? "") : "");
  const [dueNumber, setDueNumber] = useState(editor.type === "task" ? (editor.data?.due_number ?? "") : "");
  const [duePeriod, setDuePeriod] = useState(editor.type === "task" ? (editor.data?.due_period ?? "days") : "days");
  const [weekInMonth, setWeekInMonth] = useState(editor.type === "task" ? (editor.data?.weekinmonth ?? "") : "");
  const [dayOfWeek, setDayOfWeek] = useState(editor.type === "task" ? (editor.data?.dayofweek !== undefined && editor.data?.dayofweek !== null ? String(editor.data?.dayofweek) : "") : "");
  const [dateStart, setDateStart] = useState(editor.type === "task" ? (editor.data?.date_start ?? "") : "");
  const [yearMonth, setYearMonth] = useState(editor.type === "task" ? (editor.data?.yearmonth ?? "") : "");
  const [yearDay, setYearDay] = useState(editor.type === "task" ? (editor.data?.yearday ?? "") : "");
  const [recordKpiType, setRecordKpiType] = useState(
    editor.type === "task"
      ? (editor.data?.record_kpi_type !== undefined && editor.data?.record_kpi_type !== null
          ? String(editor.data.record_kpi_type)
          : "")
      : ""
  );
  const [followTaskTid, setFollowTaskTid] = useState(editor.type === "task" ? (editor.data?.followstask ?? "") : "");
  const [availableForms, setAvailableForms] = useState([]);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [availableLists, setAvailableLists] = useState([]);

  // When Type is set to Yes/No (3), ensure Form field is cleared and hidden
  useEffect(() => {
    if (editor.type !== 'task') return;
    if (String(recordKpiType) === '3' && formTid) {
      setFormTid('');
    }
  }, [editor.type, recordKpiType]);

  useEffect(() => {
    // Load options
    if (editor.type === 'role') {
      (async () => {
        try {
          const usersRes = await axios.get(API_ENDPOINTS.user?.all || '/api/v1/user/all');
          const users = (usersRes?.data?.data || usersRes?.data || []).map(u => ({ value: String(u.id), label: u.name || u.email || `User #${u.id}` }));
          setAvailableUsers(users);
        } catch (e) {
          console.error('Failed to load users', e);
          setAvailableUsers([]);
        }
        try {
          const groupsRes = await axios.get(API_ENDPOINTS.manual?.options?.groups || '/api/v1/manual/options/groups');
          const groups = (groupsRes?.data?.data || groupsRes?.data || []).map(g => ({ value: String(g.unique_name || g.uid || g.name || g.title).toUpperCase(), label: `[Group] ${g.title || g.unique_name}` }));
          setAvailableGroups(groups);
        } catch (e) {
          console.error('Failed to load assignment groups', e);
          setAvailableGroups([]);
        }
      })();
    }
    if (editor.type !== 'task') return;
    (async () => {
      try {
        // Forms (active)
        const formsRes = await axios.get(API_ENDPOINTS.forms?.active || '/api/forms/active');
        const formItems = (formsRes?.data?.data || []).map(f => ({ value: f.tid, label: `${f.title} (${f.tid})` }));
        if (formTid && !formItems.find(i => i.value === formTid)) {
          formItems.unshift({ value: formTid, label: formTid + ' (not in active list)' });
        }
        setAvailableForms(formItems);
      } catch (e) {
        console.error('Failed to load forms', e);
        setAvailableForms([]);
      }
      try {
        // Tasks (for follow-on task)
        const tasksRes = await axios.get(API_ENDPOINTS.manual.options.tasks);
        const taskItems = (tasksRes?.data?.data || []).map(t => ({ value: t.tid, label: t.title }));
        if (followTaskTid && !taskItems.find(i => i.value === followTaskTid)) {
          taskItems.unshift({ value: followTaskTid, label: followTaskTid + ' (not in list)' });
        }
        setAvailableTasks(taskItems);
      } catch (e) {
        console.error('Failed to load tasks options', e);
        setAvailableTasks([]);
      }
      try {
        // Lists (for list_tid)
        const listsRes = await axios.get(API_ENDPOINTS.manual.options.lists);
        const listItems = (listsRes?.data?.data || []).map(l => ({ value: l.tid, label: l.title }));
        if (listTid && !listItems.find(i => i.value === listTid)) {
          listItems.unshift({ value: listTid, label: listTid + ' (not in list)' });
        }
        setAvailableLists(listItems);
      } catch (e) {
        console.error('Failed to load lists options', e);
        setAvailableLists([]);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After users/groups load, ensure selected values align to available options
  useEffect(() => {
    if (editor.type !== 'role') return;
    const allowedUsers = new Set((availableUsers || []).map(u => String(u.value)));
    const allowedGroups = new Set((availableGroups || []).map(g => String(g.value)));
    const isAllowed = (v) => allowedUsers.has(String(v)) || allowedGroups.has(String(v));
    if (allowedUsers.size + allowedGroups.size > 0) {
      setAssignedUsers(prev => prev.filter(isAllowed));
      setReviewUsers(prev => prev.filter(isAllowed));
    }
  }, [editor.type, availableUsers, availableGroups]);
  const [active, setActive] = useState(editor.type === "task" ? (editor.data?.active ?? true) : true);

  const save = async () => {
    if (!title || !title.trim()) return alert("Title is required");
    try {
      if (editor.type === "unit") {
        const payload = { title, description, color };
        if (isEdit) {
          await axios.put(API_ENDPOINTS.manual.unit(editor.data.id), payload);
        } else {
          await axios.post(API_ENDPOINTS.manual.units, payload);
        }
      } else if (editor.type === "role") {
        const payload = { title, description, assignedusers: assignedUsers, reviewdusers: reviewUsers };
        if (isEdit) {
          await axios.put(API_ENDPOINTS.manual.role(editor.data.id), payload);
        } else {
          await axios.post(API_ENDPOINTS.manual.roles(editor.parentId), payload);
        }
      } else if (editor.type === "task") {
        const payload = {
          title,
          description,
          create_frequency: createFrequency === "" ? null : Number(createFrequency),
          taskdays,
          reviewable,
          form_tid: formTid || null,
          list_tid: listTid || null,
          due_option: dueOption || null,
          due_number: dueOption === 'user_select' ? null : (dueNumber !== '' ? Number(dueNumber) : null),
          due_period: dueOption === 'user_select' ? null : (duePeriod || null),
          weekinmonth: weekInMonth !== '' ? Number(weekInMonth) : null,
          dayofweek: dayOfWeek !== '' ? Number(dayOfWeek) : null,
          date_start: dateStart !== '' ? Number(dateStart) : null,
          record_kpi_type: recordKpiType !== '' ? Number(recordKpiType) : null,
          followstask: followTaskTid || null,
          yearmonth: yearMonth !== '' ? Number(yearMonth) : null,
          yearday: yearDay !== '' ? Number(yearDay) : null,
          active,
        };
        if (isEdit) {
          await axios.put(API_ENDPOINTS.manual.task(editor.data.id), payload);
        } else {
          await axios.post(API_ENDPOINTS.manual.tasks(editor.parentId), payload);
        }
      }
      await reloadUnits();
      if (reopenContexts) await reopenContexts();
      onSaved?.();
      onClose();
    } catch (e) {
      console.error(e);
      alert("Failed to save. Please try again.");
    }
  };

  return (
    <div className="panel with-rounded-corner panel-default">
        <div className="panel-heading" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 className="panel-title" style={{ margin: 0 }}>
            {isEdit ? "Edit" : "Create"} {editor.type === "unit" ? "Business Unit" : editor.type === "role" ? "Role" : "Task"}
          </h3>
          <div>
            <button className="btn btn-default" onClick={onClose}>Close</button>
            <button className="btn btn-primary m-l-5" onClick={save}><i className="fa fa-save" /> Save</button>
          </div>
        </div>
        <div className="panel-collapse in">
          <div className="panel-body">
            <div style={{ display: 'grid', gridTemplateColumns: editor.type === 'task' ? 'minmax(320px, 1fr) minmax(300px, 380px)' : '1fr', gap: 16 }}>
              <div>
                <TextField label="Title" value={title} onChange={setTitle} />
                {editor.type === "unit" && (() => {
                  // Helpers for color normalization
                  const toHex6 = (v) => {
                    let s = String(v || '').trim().toLowerCase();
                    if (!s) return '';
                    if (!s.startsWith('#')) s = '#' + s;
                    const short = /^#([0-9a-f]{3})$/;
                    const full = /^#([0-9a-f]{6})$/;
                    if (short.test(s)) {
                      const h = s.slice(1);
                      s = '#' + h.split('').map((c) => c + c).join('');
                    }
                    if (full.test(s)) return s;
                    return '';
                  };
                  const pickerValue = toHex6(color) || '#2d8cff';
                  return (
                    <div className="form-group">
                      <label>Colour</label>
                      <div style={{ display: 'grid', gridTemplateColumns: '64px 1fr', gap: 8, alignItems: 'center' }}>
                        <input
                          type="color"
                          className="form-control"
                          value={pickerValue}
                          onChange={(e) => {
                            const v = e.target.value;
                            setColor(v);
                          }}
                          title="Pick a colour"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={color ?? ''}
                          onChange={(e) => {
                            // Allow free typing/paste; normalize on blur
                            setColor(e.target.value);
                          }}
                          onBlur={(e) => {
                            const norm = toHex6(e.target.value);
                            if (norm) setColor(norm);
                            else setColor(e.target.value.trim());
                          }}
                          placeholder="#2d8cff"
                          spellCheck={false}
                        />
                      </div>
                      <small className="text-muted">Use the picker or paste a hex like #2d8cff</small>
                    </div>
                  );
                })()}
                <RichTextField label="Description" value={description} onChange={setDescription} />
                {editor.type === 'role' && (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
                    <div className="form-group">
                      <label>Assigned User(s)</label>
                      <select multiple className="form-control" value={assignedUsers}
                              onChange={(e) => {
                                const vals = Array.from(e.target.selectedOptions).map(o => o.value);
                                if (vals.includes('')) {
                                  // Selecting the blank option means clear all assignments
                                  setAssignedUsers([]);
                                } else {
                                  setAssignedUsers(vals);
                                }
                              }}>
                        <option value="">— None —</option>
                        {availableUsers.map(u => (
                          <option key={u.value} value={u.value}>{u.label}</option>
                        ))}
                        {availableGroups.length > 0 && (
                          <optgroup label="Groups">
                            {availableGroups.map(g => (
                              <option key={g.value} value={g.value}>{g.label}</option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                      <small className="text-muted">Hold Ctrl/Cmd to select multiple.</small>
                    </div>
                    <div className="form-group">
                      <label>Review User(s)</label>
                      <select multiple className="form-control" value={reviewUsers}
                              onChange={(e) => {
                                const vals = Array.from(e.target.selectedOptions).map(o => o.value);
                                if (vals.includes('')) {
                                  // Selecting the blank option means clear all reviewers
                                  setReviewUsers([]);
                                } else {
                                  setReviewUsers(vals);
                                }
                              }}>
                        <option value="">— None —</option>
                        {availableUsers.map(u => (
                          <option key={u.value} value={u.value}>{u.label}</option>
                        ))}
                        {availableGroups.length > 0 && (
                          <optgroup label="Groups">
                            {availableGroups.map(g => (
                              <option key={g.value} value={g.value}>{g.label}</option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </div>
                  </div>
                )}
              </div>
              {editor.type === "task" && (
                <div>
                  <SelectField
                    label="Create Frequency"
                    value={createFrequency}
                    onChange={(v) => setCreateFrequency(String(v))}
                    options={[
                      { value: 3, label: "Manually" },
                      { value: 2, label: "On Button" },
                      { value: 1, label: "On Trigger (after task)" },
                      { value: 14, label: "Day of Month" },
                      { value: 7, label: "First work day (monthly)" },
                      { value: 8, label: "Last work day (monthly)" },
                      { value: 10, label: "Monthly" },
                      { value: 11, label: "Weekly" },
                      { value: 15, label: "Yearly" },
                    ]}
                  />

                  {/* Frequency-dependent sections */}
                  {String(createFrequency) === '1' && (
                    <div className="p-10 well">
                      <label>Auto Create after Task</label>
                      {availableTasks.length > 0 ? (
                        <SelectField label="Follow Task" value={followTaskTid} onChange={setFollowTaskTid} options={availableTasks} />
                      ) : (
                        <TextField label="Follow Task TID" value={followTaskTid} onChange={setFollowTaskTid} />
                      )}
                      <div className="form-group">
                        <label>Set Due Date after parent completion</label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
                          <div>
                            <TextField label="Due #" value={dueNumber} onChange={setDueNumber} type="number" />
                          </div>
                          <div>
                            <SelectField label="Period" value={duePeriod} onChange={setDuePeriod} options={[
                              { value: 'minutes', label: 'Minutes' },
                              { value: 'hours', label: 'Hours' },
                              { value: 'days', label: 'Days' },
                              { value: 'weeks', label: 'Weeks' },
                              { value: 'months', label: 'Months' },
                            ]} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {String(createFrequency) === '10' && (
                    <SelectField label="Recurring on day" value={dateStart} onChange={setDateStart} options={Array.from({length:31}, (_,i)=>({ value: i+1, label: String(i+1)}))} />
                  )}

                  {String(createFrequency) === '11' && (
                    <WeekdayToggleGroup label="On day(s)" value={taskdays} onChange={setTaskdays} />
                  )}

                  {String(createFrequency) === '14' && (
                    <>
                      <SelectField label="Week in month" value={weekInMonth} onChange={setWeekInMonth} options={[1,2,3,4,5].map(v=>({value:v,label:String(v)}))} />
                      <SelectField label="Day of week" value={dayOfWeek} onChange={setDayOfWeek} options={[
                        {value:'1',label:'Monday'},{value:'2',label:'Tuesday'},{value:'3',label:'Wednesday'},{value:'4',label:'Thursday'},{value:'5',label:'Friday'},{value:'6',label:'Saturday'},{value:'7',label:'Sunday'}
                      ]} />
                    </>
                  )}

                  {String(createFrequency) === '15' && (
                    <>
                      <SelectField label="Month" value={yearMonth} onChange={setYearMonth} options={Array.from({length:12},(_,i)=>({value:i+1,label:new Date(2000,i).toLocaleString('en',{month:'long'})}))} />
                      <SelectField label="Day" value={yearDay} onChange={setYearDay} options={Array.from({length:31},(_,i)=>({value:i+1,label:String(i+1)}))} />
                    </>
                  )}

                  {/* Generic due settings */}
                  <SelectField label="Due Option" value={dueOption} onChange={setDueOption} options={[{value:'',label:'Auto'},{value:'user_select',label:'Show Popup Calendar'}]} />
                  {dueOption !== 'user_select' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
                      <div><TextField label="Due #" value={dueNumber} onChange={setDueNumber} type="number" /></div>
                      <div><SelectField label="Period" value={duePeriod} onChange={setDuePeriod} options={[
                        { value: 'minutes', label: 'Minutes' },
                        { value: 'hours', label: 'Hours' },
                        { value: 'days', label: 'Days' },
                        { value: 'weeks', label: 'Weeks' },
                        { value: 'months', label: 'Months' },
                      ]} /></div>
                    </div>
                  )}

                  {/* Other task meta */}
                  <SelectField label="Type" value={recordKpiType} onChange={setRecordKpiType} options={[
                    {value:3,label:'Yes/No'},
                    {value:4,label:'Form'},
                  ]} />
                  {String(recordKpiType) === '4' && (
                    <SelectField label="Form" value={formTid} onChange={setFormTid} options={availableForms} />
                  )}
                  {availableLists.length > 0 ? (
                    <SelectField label="List" value={listTid} onChange={setListTid} options={availableLists} />
                  ) : (
                    <TextField label="List TID" value={listTid} onChange={setListTid} />
                  )}
                  <small className="text-muted">
                    Note: This List link is currently informational only for task definitions and isn’t
                    used by the task scheduler or task instances. It’s safe to leave blank.
                  </small>
                  <CheckboxField label="Reviewable" checked={reviewable} onChange={setReviewable} />
                  <CheckboxField label="Active" checked={active} onChange={setActive} />
                </div>
              )}
            </div>
          </div>
        </div>
    </div>
  );
};

const ManualManager = () => {
  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [editor, setEditor] = useState(null); // { type: 'unit'|'role'|'task', mode:'create'|'edit', data?, parentId? }
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unitsViewMode, setUnitsViewMode] = useState('panels'); // 'panels' | 'table'
  const [tasksViewMode, setTasksViewMode] = useState('list'); // for RoleView

  const history = useAsync(async (taskId, limit = 25) => {
    const { data } = await axios.get(API_ENDPOINTS.manual.taskHistory(taskId, limit));
    return data;
  }, []);

  const normalizeRole = (role) => {
    const norm = { ...role };
    const toNames = (val) => {
      if (Array.isArray(val)) return val;
      if (val && typeof val === 'object') return Object.values(val);
      return [];
    };
    const toIds = (val) => {
      if (Array.isArray(val)) return val.map(String);
      if (val && typeof val === 'object') return Object.keys(val).map(String);
      return [];
    };
    // Preserve maps (if provided) and add derived arrays for both names and ids
    norm.assigned_to_map = (role.assigned_to && typeof role.assigned_to === 'object' && !Array.isArray(role.assigned_to)) ? role.assigned_to : null;
    norm.reviewed_by_map = (role.reviewed_by && typeof role.reviewed_by === 'object' && !Array.isArray(role.reviewed_by)) ? role.reviewed_by : null;
    norm.assigned_to_names = toNames(role.assigned_to);
    norm.reviewed_by_names = toNames(role.reviewed_by);
    norm.assigned_to_ids = toIds(role.assigned_to);
    norm.reviewed_by_ids = toIds(role.reviewed_by);
    return norm;
  };

  const loadUnits = async () => {
    setLoadingUnits(true);
    try {
      const { data } = await axios.get(API_ENDPOINTS.manual.units);
      const baseUnits = data.data || [];
      // fetch roles for each unit
      const withRoles = await Promise.all(
        baseUnits.map(async (u) => {
          const r = await axios.get(API_ENDPOINTS.manual.roles(u.id));
          const roles = (r.data.data || []).map(normalizeRole);
          return { ...u, roles };
        })
      );
      setUnits(withRoles);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingUnits(false);
    }
  };

  const openRole = async (roleOrId) => {
    try {
      const roleId = typeof roleOrId === 'object' ? roleOrId.id : roleOrId;
      // Always fetch the latest role details from API to avoid stale state
      let baseRole = null;
      try {
        const roleRes = await axios.get(API_ENDPOINTS.manual.role(roleId));
        const roleData = roleRes?.data?.data || null;
        baseRole = roleData ? normalizeRole(roleData) : null;
      } catch (err) {
        // Fallback to local data if API not available
        let freshRole = null;
        for (const u of units) {
          const found = (u.roles || []).find(r => r.id === roleId);
          if (found) { freshRole = found; break; }
        }
        baseRole = freshRole ? normalizeRole(freshRole) : (typeof roleOrId === 'object' ? normalizeRole(roleOrId) : { id: roleId });
      }
      const { data } = await axios.get(API_ENDPOINTS.manual.tasks(roleId));
      setSelectedRole({ ...baseRole, tasks: data.data || [] });
      setSelectedTask(null);
    } catch (e) {
      console.error(e);
    }
  };

  const openTask = async (task) => {
    setSelectedTask(task);
    await history.run(task.id);
  };

  useEffect(() => {
    loadUnits();
  }, []);

  // Mutations
  const addUnit = async () => {
    setEditor({ type: "unit", mode: "create" });
    setDrawerOpen(true);
  };

  const editUnit = async (unit) => {
    setEditor({ type: "unit", mode: "edit", data: unit });
    setDrawerOpen(true);
  };

  const deleteUnit = async (unit) => {
    if (!confirm(`Delete unit "${unit.title}"?`)) return;
    await axios.delete(API_ENDPOINTS.manual.unit(unit.id));
    await loadUnits();
  };

  const addRole = async (unit) => {
    setEditor({ type: "role", mode: "create", parentId: unit.id });
    setDrawerOpen(true);
  };

  const editRole = async (role) => {
    setEditor({ type: "role", mode: "edit", data: role });
    setDrawerOpen(true);
  };

  const deleteRole = async (role) => {
    if (!confirm(`Delete role "${role.title}"?`)) return;
    await axios.delete(API_ENDPOINTS.manual.role(role.id));
    setSelectedRole(null);
    await loadUnits();
  };

  const addTask = async (role) => {
    setEditor({ type: "task", mode: "create", parentId: role.id });
    setDrawerOpen(true);
  };

  const editTask = async (task) => {
    setEditor({ type: "task", mode: "edit", data: task });
    setDrawerOpen(true);
  };

  const deleteTask = async (task) => {
    if (!confirm(`Delete task "${task.title}"?`)) return;
    await axios.delete(API_ENDPOINTS.manual.task(task.id));
    setSelectedTask(null);
    await openRole(selectedRole);
    await loadUnits();
  };

  return (
    <div className="container-fluid">
      <div>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
            <h2 className="m-b-0">Business Operations Manual</h2>
            {!selectedRole && !selectedTask && (
              <div>
                <span className="m-r-10">View:</span>
                <div className="btn-group">
                  <button className={`btn btn-sm ${unitsViewMode==='panels'?'btn-primary':'btn-default'}`} onClick={() => setUnitsViewMode('panels')}>Panels</button>
                  <button className={`btn btn-sm ${unitsViewMode==='table'?'btn-primary':'btn-default'}`} onClick={() => setUnitsViewMode('table')}>Table</button>
                </div>
                <button className="btn btn-sm btn-primary m-l-10" onClick={addUnit}><i className="fa fa-plus" /> New Unit</button>
              </div>
            )}
          </div>
          {!selectedRole && !selectedTask && (
            <p>
              Your operations manual is compiled from the business units, roles and tasks structure in the Tree View.
              It’s a summary of how your business operates and is dynamically driven by the tasks system.
              Each area represents a breakdown of how your business operates.
            </p>
          )}
      </div>

      {!selectedRole && !selectedTask && (
        <div>
          {loadingUnits && <div>Loading…</div>}
          {!loadingUnits && unitsViewMode === 'panels' && (
            <div style={gridListStyle}>
              {units.map((u) => (
                <UnitCard key={u.id}
                  unit={u}
                  onEdit={editUnit}
                  onDelete={deleteUnit}
                  onAddRole={addRole}
                  onOpenRole={openRole}
                />
              ))}
            </div>
          )}
          {!loadingUnits && unitsViewMode === 'table' && (
            <div className="panel with-rounded-corner panel-default">
                <div className="panel-heading">
                  <div className="panel-heading-btn">
                    <button className="btn btn-primary btn-sm" onClick={addUnit}><i className="fa fa-plus" /> New Unit</button>
                  </div>
                  <h3 className="panel-title">Units</h3>
                </div>
                <div className="panel-collapse in">
                  <div className="panel-body">
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Title</th>
                            <th>TID</th>
                            <th>Colour</th>
                            <th>Roles</th>
                            <th>Description</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {units.map((u) => (
                            <React.Fragment key={u.id}>
                              <tr>
                                <td>{u.title}</td>
                                <td>{u.tid}</td>
                                <td>
                                  <span style={{ display:'inline-block', width:14, height:14, background:u.color || '#888', borderRadius:2 }} />
                                </td>
                                <td>{Array.isArray(u.roles)? u.roles.length : 0}</td>
                                <td>
                                  <small style={{ color:'#666' }} dangerouslySetInnerHTML={{ __html: (u.description || '').slice(0, 120) + (u.description && u.description.length > 120 ? '…' : '') }} />
                                </td>
                                <td>
                                  <button className="btn btn-xs btn-default m-r-5" onClick={() => editUnit(u)}><i className="fa fa-pencil" /></button>
                                  <button className="btn btn-xs btn-default m-r-5" onClick={() => addRole(u)}><i className="fa fa-user-plus" /></button>
                                  <button className="btn btn-xs btn-danger" onClick={() => deleteUnit(u)}><i className="fa fa-trash" /></button>
                                </td>
                              </tr>
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
          )}
        </div>
      )}

      {selectedRole && !selectedTask && (
        <div>
          <RoleView
            role={selectedRole}
            onBack={() => setSelectedRole(null)}
            onEdit={editRole}
            onDelete={deleteRole}
            onAddTask={addTask}
            onOpenTask={openTask}
            tasksViewMode={tasksViewMode}
            setTasksViewMode={setTasksViewMode}
          />
        </div>
      )}

      {selectedTask && (
        <div>
          <TaskView
            task={selectedTask}
            onBack={() => setSelectedTask(null)}
            history={history}
            onEdit={editTask}
            onDelete={deleteTask}
          />
        </div>
      )}

      {/* Drawer for create/edit forms */}
      {drawerOpen && editor && (
        <>
          <div
            onClick={() => setDrawerOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1040 }}
          />
          <div style={{ position: 'fixed', top: 0, right: 0, height: '100%', width: 'min(95vw, 960px)', background: '#fff', zIndex: 1050, boxShadow: '0 0 0 1px rgba(0,0,0,0.05), 0 10px 30px rgba(0,0,0,0.2)' }}>
            <div className="panel-heading" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #eee' }}>
              <h3 className="panel-title" style={{ margin: 0 }}>
                {(editor.mode === 'edit' ? 'Edit ' : 'Create ') + (editor.type === 'unit' ? 'Business Unit' : editor.type === 'role' ? 'Role' : 'Task')}
              </h3>
              <button className="btn btn-default" onClick={() => setDrawerOpen(false)}><i className="fa fa-times" /></button>
            </div>
            <div style={{ height: 'calc(100% - 110px)', overflowY: 'auto', padding: 16 }}>
              <EditorPanel
                editor={editor}
                onClose={() => { setDrawerOpen(false); setEditor(null); }}
                reloadUnits={loadUnits}
                reopenContexts={async () => {
                  if (selectedRole) await openRole(selectedRole.id);
                  if (selectedTask) await history.run(selectedTask.id);
                }}
              />
            </div>
          </div>
        </>
      )}

    </div>
  );
};

export default ManualManager;
